import React from 'react';
import { Button, Typography, Collapse, Progress, Image } from 'components';
import { FullscreenOutlined } from '@ant-design/icons';
import type { UploadFile } from 'components';
import { ArrowBottomIcon } from 'components/common/icons/ArrowBottomIcon';
import { ArrowLeftIcon } from 'components/common/icons/ArrowLeftIcon';
import { useOutboundOrderDetails } from './useOutboundOrderDetails';
import { StatusBadge } from 'components/warehouse';
import styles from './styles.module.scss';

const { Title, Text } = Typography;

export const OutboundOrderDetails: React.FC = () => {
  const {
    order,
    isLoading,
    fileListByItem,
    previewOpen,
    previewImage,
    setPreviewOpen,
    setPreviewImage,
    handlePreview,
    handleGoToSource,
    handleBack,
  } = useOutboundOrderDetails();

  if (isLoading || !order) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Text>Loading order details...</Text>
        </div>
      </div>
    );
  }

  const totalItems = order.itemsResponseList?.length || 0;

  return (
    <div className={styles.container}>
      {/* Back Button */}
      <div className={styles.backButtonContainer}>
        <Button type="text" onClick={handleBack} className={styles.backButton} icon={<ArrowLeftIcon />} />
      </div>

      <div className={styles.header}>
        <div className={styles.orderInfo}>
          <div className={styles.orderTitleRow}>
            <Title level={4} className={styles.orderTitle}>
              Order: {order.uuid}
            </Title>
            <StatusBadge status={order.status} type="outbound" />
          </div>
          <Text className={styles.clientName}>Client: {order.clientName}</Text>
          {order.clientRequestNumber && (
            <Text className={styles.clientRequestNumber}>Request Number: {order.clientRequestNumber}</Text>
          )}
        </div>

        <div className={styles.progressSection}>
          <Progress percent={100} strokeColor="#05796B" showInfo={false} className={styles.progressBar} />
          <div className={styles.progressHeader}>
            <Text className={styles.progressLabel}>Completed Items</Text>
            <Text className={styles.progressValue}>{totalItems}</Text>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.itemsList}>
          {order.itemsResponseList?.map((item) => (
            <div key={item.id} className={styles.itemCard}>
              <Collapse
                ghost
                expandIconPosition="end"
                className={styles.collapse}
                expandIcon={({ isActive }) => (
                  <div
                    className={styles.expandIconWrapper}
                    style={{ transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  >
                    <ArrowBottomIcon />
                  </div>
                )}
                items={[
                  {
                    key: item.id,
                    label: (
                      <div className={styles.itemHeader}>
                        <Text className={styles.itemSku}>{item.sku}</Text>
                        <StatusBadge status={item.status} type="item" />
                      </div>
                    ),
                    children: (
                      <div className={styles.itemDetails}>
                        <div className={styles.quantityRow}>
                          <Text className={styles.quantityText}>
                            <span className={styles.quantityLabel}>Quantity:</span>{' '}
                            <span className={styles.quantityValue}>{item.quantity} item</span>
                          </Text>
                        </div>
                        {item.description && (
                          <div className={styles.descriptionRow}>
                            <Text className={styles.descriptionText}>{item.description}</Text>
                          </div>
                        )}

                        {/* Display images with preview only - no upload, no delete */}
                        {fileListByItem[item.id] && fileListByItem[item.id].length > 0 && (
                          <div className={styles.imagesSection}>
                            <Text className={styles.imagesSectionTitle}>Images</Text>
                            <div className={styles.imagesGrid}>
                              {fileListByItem[item.id].map((file: UploadFile) => (
                                <div key={file.uid} className={styles.imageCard}>
                                  <img src={file.url} alt={file.name} className={styles.imagePreview} />
                                  <div className={styles.imageOverlay}>
                                    <Button
                                      type="text"
                                      icon={<FullscreenOutlined />}
                                      onClick={() => handlePreview(file)}
                                      className={styles.previewButton}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Go To Source Button */}
                        <div className={styles.itemActions}>
                          <Button
                            type="link"
                            className={styles.goToSourceButton}
                            onClick={() => handleGoToSource(item)}
                          >
                            Go To Source
                          </Button>
                        </div>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          ))}
        </div>
      </div>

      {previewImage && (
        <Image
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(''),
          }}
          src={previewImage}
        />
      )}
    </div>
  );
};
